import { CommonActivityType } from './common-activity-type.enum';

/**
 * Activity types tracked in the opportunity_activity table.
 * Extends CommonActivityType with Opportunity-specific types.
 */
export const OpportunityActivityType = {
	...CommonActivityType,
	// Opportunity-specific types
	STAGE_CHANGED: 'stage_changed',
	VALUE_CHANGED: 'value_changed',
	CLOSE_DATE_CHANGED: 'close_date_changed',
	// PD-17797 — every other edit reaches the feed and its watchers.
	DETAILS_CHANGED: 'details_changed',
	QUOTE_CHANGED: 'quote_changed',
	COMPETITOR_CHANGED: 'competitor_changed',
	PERSON_CHANGED: 'person_changed',
	JOB_CHANGED: 'job_changed',
} as const;

export type OpportunityActivityType =
	(typeof OpportunityActivityType)[keyof typeof OpportunityActivityType];

/**
 * Labels for activity types (for display in filter bar)
 */
export const OPPORTUNITY_ACTIVITY_TYPE_LABELS: Record<
	OpportunityActivityType,
	string
> = {
	// Common types
	[OpportunityActivityType.CREATED]: 'Created',
	[OpportunityActivityType.UPDATED]: 'Updated',
	[OpportunityActivityType.DELETED]: 'Deleted',
	[OpportunityActivityType.RECOVERED]: 'Recovered',
	[OpportunityActivityType.STATUS_CHANGED]: 'Status Changed',
	[OpportunityActivityType.PRIORITY_CHANGED]: 'Priority Changed',
	[OpportunityActivityType.ASSIGNEE_CHANGED]: 'Assignee Changed',
	[OpportunityActivityType.NOTE_ADDED]: 'Note Added',
	[OpportunityActivityType.NOTE_UPDATED]: 'Note Updated',
	[OpportunityActivityType.NOTE_DELETED]: 'Note Deleted',
	[OpportunityActivityType.TASK_LINKED]: 'Task Linked',
	[OpportunityActivityType.TASK_UNLINKED]: 'Task Unlinked',
	[OpportunityActivityType.TASK_CREATED]: 'Task Created',
	[OpportunityActivityType.TASK_COMPLETED]: 'Task Completed',
	[OpportunityActivityType.TASK_UPDATED]: 'Task Updated',
	[OpportunityActivityType.TASK_DELETED]: 'Task Deleted',
	[OpportunityActivityType.FILE_ATTACHED]: 'File Attached',
	[OpportunityActivityType.FILE_REMOVED]: 'File Removed',
	[OpportunityActivityType.ENTITY_LINKED]: 'Entity Linked',
	[OpportunityActivityType.ENTITY_UNLINKED]: 'Entity Unlinked',
	[OpportunityActivityType.WATCHER_ADDED]: 'Watcher Added',
	[OpportunityActivityType.WATCHER_REMOVED]: 'Watcher Removed',
	[OpportunityActivityType.AI_SESSION]: 'AI Session',
	// Opportunity-specific types
	[OpportunityActivityType.STAGE_CHANGED]: 'Stage Changed',
	[OpportunityActivityType.VALUE_CHANGED]: 'Value Changed',
	[OpportunityActivityType.CLOSE_DATE_CHANGED]: 'Close Date Changed',
	[OpportunityActivityType.DETAILS_CHANGED]: 'Details Changed',
	[OpportunityActivityType.QUOTE_CHANGED]: 'Quote Changed',
	[OpportunityActivityType.COMPETITOR_CHANGED]: 'Competitor Changed',
	[OpportunityActivityType.PERSON_CHANGED]: 'Person Changed',
	[OpportunityActivityType.JOB_CHANGED]: 'Job Changed',
};

/**
 * Group activity types for filter bar categories
 */
export const OPPORTUNITY_ACTIVITY_GROUPS = {
	all: [] as Array<OpportunityActivityType>,
	status: [
		OpportunityActivityType.CREATED,
		OpportunityActivityType.UPDATED,
		OpportunityActivityType.STATUS_CHANGED,
		OpportunityActivityType.ASSIGNEE_CHANGED,
	],
	stages: [
		OpportunityActivityType.STAGE_CHANGED,
		OpportunityActivityType.VALUE_CHANGED,
		OpportunityActivityType.CLOSE_DATE_CHANGED,
	],
	notes: [
		OpportunityActivityType.NOTE_ADDED,
		OpportunityActivityType.NOTE_UPDATED,
		OpportunityActivityType.NOTE_DELETED,
	],
	tasks: [
		OpportunityActivityType.TASK_LINKED,
		OpportunityActivityType.TASK_UNLINKED,
		OpportunityActivityType.TASK_CREATED,
		OpportunityActivityType.TASK_COMPLETED,
	],
	files: [
		OpportunityActivityType.FILE_ATTACHED,
		OpportunityActivityType.FILE_REMOVED,
	],
	watchers: [
		OpportunityActivityType.WATCHER_ADDED,
		OpportunityActivityType.WATCHER_REMOVED,
	],
};
